// Interne Editor-Typen + Mapping zum persistierten RoofSegment.
import type { LatLng } from "./geo.js";
import type { RoofSemanticData } from "@energyos/shared-types";
import { normalizeRoofSemanticData } from "@energyos/shared-types";

export type Tool = "select" | "pan" | "rect" | "polygon" | "measure" | "angle" | "edge" | "keepout" | "module";
export type DetailsTab = "gebaeude" | "stoer";

/** Auswahl eines semantischen Objekts (Kante/Sperrfläche) auf der Karte (Stage 2b). */
export interface SemanticSelection {
  surfaceId: string;
  kind: "edge" | "keepout";
  id: string;
}
export type BgSource = "canvas" | "sat" | "hd";

/** Persistiertes Segment (Vertrag mit ProjectDetailPage / Backend). */
export interface RoofSegment {
  id: string;
  label: string;
  tiltDeg: number;
  azimuthDeg: number; // = Nordwinkel
  areaM2: number;
  polygon?: LatLng[];
  color: string;
  isObstacle?: boolean;
  // pvXpert-Dacheditor (additiv)
  heightZ?: number | null;
  rotDeg?: number;
  scalePct?: number;
  projektion?: boolean;
  locked?: boolean;
  hidden?: boolean;
  // Stage 2: semantische Objekte (Dachkanten + Sperrflächen) je Fläche.
  semanticJson?: RoofSemanticData;
}

/** Editor-internes Objekt — Polygon ist hier immer gesetzt. */
export interface EditorObject {
  id: string;
  kind: "roof" | "obstacle";
  name: string;
  color: string;
  polygon: LatLng[];
  tiltDeg: number;
  northDeg: number;
  heightZ: number;
  rotDeg: number;
  scalePct: number;
  projektion: boolean;
  locked: boolean;
  hidden: boolean;
  // Stage 2: normalisierte semantische Daten (immer gesetzt im Editor).
  semantic: RoofSemanticData;
}

export const ROOF_COLORS = [
  "#2563eb",
  "#16a34a",
  "#d97706",
  "#dc2626",
  "#7c3aed",
  "#0891b2",
];
export const OBSTACLE_COLOR = "#d97706";

export function segmentToObject(seg: RoofSegment): EditorObject {
  return {
    id: seg.id,
    kind: seg.isObstacle ? "obstacle" : "roof",
    name: seg.label,
    color: seg.color,
    polygon: seg.polygon ?? [],
    tiltDeg: seg.tiltDeg,
    northDeg: seg.azimuthDeg,
    heightZ: seg.heightZ ?? 0,
    rotDeg: seg.rotDeg ?? 0,
    scalePct: seg.scalePct ?? 100,
    projektion: seg.projektion ?? false,
    locked: seg.locked ?? false,
    hidden: seg.hidden ?? false,
    semantic: normalizeRoofSemanticData(seg.semanticJson),
  };
}

/** Stabile Signatur, um Re-Seeding nur bei echter Inhaltsänderung auszulösen. */
export function segmentsSignature(segs: RoofSegment[]): string {
  return segs
    .map(
      (s) =>
        `${s.id}:${s.label}:${s.tiltDeg}:${s.azimuthDeg}:${s.areaM2}:${s.heightZ ?? ""}:${s.rotDeg ?? ""}:${s.scalePct ?? ""}:${s.projektion ?? ""}:${s.locked ?? ""}:${s.hidden ?? ""}:${(s.polygon ?? []).length}:e${s.semanticJson?.edges.length ?? 0}:k${s.semanticJson?.keepouts.length ?? 0}:d${s.semanticJson?.disabledModules?.length ?? 0}`,
    )
    .join("|");
}
